import { FC, Fragment } from 'react';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Control, UseFormGetValues } from 'react-hook-form';
import { Column } from 'further-ui/components/Layout';
import { PeriodEndReportFormValues } from '../schema';
import { ToggleableFieldConfig } from './types';
import FieldGroup from './FieldGroup';
import ToggleableFieldRow from './ToggleableFieldRow';
import ReadOnlyDatapoint from './ReadOnlyDatapoint';

const investmentFocusFields: Array<ToggleableFieldConfig> = [
  {
    label: 'Stage',
    valuePath: 'fundOverviewDatapoints.investmentFocus.stage.value',
    includedPath: 'fundOverviewDatapoints.investmentFocus.stage.included',
    placeholder: 'Enter stage',
    readOnly: false,
  },
  {
    label: 'Sector',
    valuePath: 'fundOverviewDatapoints.investmentFocus.sector.value',
    includedPath: 'fundOverviewDatapoints.investmentFocus.sector.included',
    placeholder: 'Enter sector',
    readOnly: false,
  },
  {
    label: 'Geography',
    valuePath: 'fundOverviewDatapoints.investmentFocus.geography.value',
    includedPath: 'fundOverviewDatapoints.investmentFocus.geography.included',
    placeholder: 'Enter geography',
    readOnly: false,
  },
];

const OuterColumn = styled(Column)({
  maxWidth: '800px',
});

type Props = {
  control: Control<PeriodEndReportFormValues>;
  readOnly: boolean;
  getValues: UseFormGetValues<PeriodEndReportFormValues>;
};

const InvestmentFocus: FC<Props> = ({ control, readOnly, getValues }) => {
  const noIncludedValues = investmentFocusFields.every(
    (config) => !getValues(config.includedPath),
  );

  if (readOnly && noIncludedValues) {
    return null;
  }

  return (
    <>
      {readOnly ? <Divider /> : null}
      <OuterColumn>
        {readOnly ? (
          <Column spacing="sm">
            <Typography variant="h5" color="grey.900">
              Investment focus
            </Typography>
            {investmentFocusFields.map((config) => {
              const isIncluded = getValues(config.includedPath);
              const value = getValues(config.valuePath);

              return isIncluded && value ? (
                <ReadOnlyDatapoint
                  key={config.valuePath}
                  label={config.label}
                  value={value}
                />
              ) : null;
            })}
          </Column>
        ) : (
          <FieldGroup title="Investment focus">
            {investmentFocusFields.map((config, index, array) => {
              const isNotLast = index < array.length - 1;

              return (
                <Fragment key={config.valuePath}>
                  <ToggleableFieldRow config={config} control={control} />
                  {isNotLast ? <Divider /> : null}
                </Fragment>
              );
            })}
          </FieldGroup>
        )}
      </OuterColumn>
    </>
  );
};

export default InvestmentFocus;
